<script setup lang="ts">
/** A notice opened over the page: a game window over a scrim, the same shape as `ui/LegalOverlay.vue`. The body is CMS HTML and goes through `sanitizeHtml`; until the full article arrives (or if it never does) the summary stands in. */
import { useI18n } from '@/i18n';
import type { NewsArticle, NewsSummary } from '@/net/api';
import GameIcon from '@/ui/kit/GameIcon.vue';
import GameWindow from '@/ui/kit/GameWindow.vue';
import SheetTransition from '@/ui/panel/SheetTransition.vue';
import { sanitizeHtml } from '@/ui/sanitize-html';

defineProps<{ notice: NewsSummary | null; article: NewsArticle | null; loading: boolean }>();
defineEmits<{ close: [] }>();
const { t } = useI18n();
</script>

<template>
  <SheetTransition>
    <GameWindow v-if="notice" width="46rem" level="dialog" aria-modal="true" :title="notice.title" icon="bell" @close="$emit('close')">
      <p v-if="loading" class="note"><GameIcon name="hourglass" :size="44" />{{ t('legal.loading') }}</p>
      <div v-else class="body" v-html="sanitizeHtml(article?.content ?? notice.summary)"></div>
    </GameWindow>
  </SheetTransition>
</template>

<style scoped>
.note {
  display: grid;
  justify-items: center;
  gap: 8px;
  margin: 16px 0;
  font-family: var(--font-game-body);
  font-weight: 600;
  color: #c9d6e2;
}

.body {
  font-family: var(--font-game-body);
  font-size: 0.95rem;
  line-height: 1.6;
  color: #e8eef4;
}

.body :deep(a) {
  color: var(--kit-gold);
}

.body :deep(img) {
  max-width: 100%;
  height: auto;
}
</style>
